import { useCallback, useSyncExternalStore } from 'react';
import { useAtom, useAtomValue } from 'jotai';
import {
  clampChatPanelWidth,
  getChatPanelMaxWidth,
} from '../../constants/chat';
import { APP_SIDEBAR_WIDTH } from '../../constants/layout';
import { appLayoutState, chatPanelWidthState } from '../../store/global.store';

const subscribeToResize = (onResize: () => void) => {
  window.addEventListener('resize', onResize);
  return () => window.removeEventListener('resize', onResize);
};

const getViewportWidth = () => window.innerWidth;

/**
 * The chat panel's on-screen width. The stored width is what the user last
 * resized to; it is clamped to the space the left sidebar currently leaves, so
 * expanding the sidebar narrows the panel and collapsing it again gives the
 * stored width back.
 */
export const useChatPanelWidth = () => {
  const [storedWidth, setStoredWidth] = useAtom(chatPanelWidthState);
  const layoutMode = useAtomValue(appLayoutState);
  const viewportWidth = useSyncExternalStore(
    subscribeToResize,
    getViewportWidth,
  );
  const sidebarWidth = APP_SIDEBAR_WIDTH[layoutMode];

  const setWidth = useCallback(
    (nextWidth: number) => {
      setStoredWidth(
        clampChatPanelWidth(nextWidth, viewportWidth, sidebarWidth),
      );
    },
    [setStoredWidth, sidebarWidth, viewportWidth],
  );

  // Stored as the whole viewport rather than today's max, so the clamp above
  // keeps the panel flush with the sidebar whether it is collapsed or expanded.
  const maximize = useCallback(() => {
    setStoredWidth(viewportWidth);
  }, [setStoredWidth, viewportWidth]);

  return {
    width: clampChatPanelWidth(storedWidth, viewportWidth, sidebarWidth),
    maxWidth: getChatPanelMaxWidth(viewportWidth, sidebarWidth),
    setWidth,
    maximize,
  };
};
